/**
 * Traducción de errores de API a mensajes aptos para el usuario final.
 *
 * Cuando un input no pasa la validación Zod de una procedure tRPC, el
 * `error.message` que llega al cliente es el array de issues serializado
 * como JSON (texto técnico en inglés) — eso nunca puede mostrarse en un
 * toast. Los mensajes de negocio del backend (TRPCError con texto en
 * español) sí se muestran tal cual.
 */

const DEFAULT_FALLBACK = "Ocurrió un error. Intentá de nuevo.";
const NETWORK_MESSAGE =
  "No pudimos conectar con el servidor. Revisá tu conexión e intentá de nuevo.";

/** Etiquetas en español para los campos validados por Zod en el backend. */
const FIELD_LABELS: Record<string, string> = {
  participationPercentage: "el % de participación",
  cuitCuil: "el DNI/CUIL",
  cuit: "el CUIT",
  dni: "el DNI",
  fullName: "el nombre completo",
  razonSocial: "la razón social",
  email: "el email",
  telefono: "el teléfono",
  phone: "el teléfono",
  beneficiaryType: "el tipo de beneficiario",
  documentType: "el tipo de documento",
};

/** Segmentos estructurales del path de Zod que no nombran un campo visible. */
const STRUCTURAL_SEGMENTS = new Set(["patch", "data", "input", "body"]);

const NETWORK_PATTERN =
  /failed to fetch|fetch failed|networkerror|econnrefused|network request failed|load failed/i;
const TECHNICAL_PATTERN =
  /unexpected token|internal server error|trpcerror|<!doctype|is not a function|cannot read propert/i;

interface ZodIssueLike {
  code?: string;
  path?: Array<string | number>;
  message?: string;
  maximum?: number | string;
  minimum?: number | string;
  received?: unknown;
}

function parseZodIssues(raw: string): ZodIssueLike[] | null {
  if (!raw.startsWith("[")) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) return null;
    const allIssues = parsed.every(
      (issue) =>
        issue !== null &&
        typeof issue === "object" &&
        typeof (issue as ZodIssueLike).message === "string",
    );
    return allIssues ? (parsed as ZodIssueLike[]) : null;
  } catch {
    return null;
  }
}

function fieldLabel(path: Array<string | number> | undefined): string {
  if (path) {
    for (let i = path.length - 1; i >= 0; i--) {
      const segment = path[i];
      if (typeof segment !== "string" || STRUCTURAL_SEGMENTS.has(segment)) continue;
      return FIELD_LABELS[segment] ?? `el campo "${segment}"`;
    }
  }
  return "uno de los datos";
}

function describeIssue(issue: ZodIssueLike): string {
  const label = fieldLabel(issue.path);
  switch (issue.code) {
    case "too_big":
      return issue.maximum !== undefined
        ? `${label} debe ser como máximo ${issue.maximum}`
        : `${label} supera el valor permitido`;
    case "too_small":
      // min(1) en strings equivale a "campo requerido"
      return issue.minimum !== undefined && Number(issue.minimum) > 1
        ? `${label} debe ser como mínimo ${issue.minimum}`
        : `${label} es obligatorio`;
    case "invalid_type":
      return issue.received === "undefined" || issue.received === "null"
        ? `${label} es obligatorio`
        : `${label} no es válido`;
    case "invalid_format":
    case "invalid_string":
      return `${label} no tiene un formato válido`;
    case "custom":
      // Los .refine() del backend suelen traer mensaje propio en español
      return issue.message && !TECHNICAL_PATTERN.test(issue.message)
        ? issue.message
        : `${label} no es válido`;
    default:
      return `${label} no es válido`;
  }
}

/**
 * Devuelve un mensaje mostrable al usuario a partir de cualquier error de
 * mutación/query. Nunca devuelve JSON ni texto técnico.
 */
export function getUserErrorMessage(
  error: unknown,
  fallback: string = DEFAULT_FALLBACK,
): string {
  const raw =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : "";
  const text = raw.trim();
  if (!text) return fallback;

  const issues = parseZodIssues(text);
  if (issues) {
    const described = issues.slice(0, 3).map(describeIssue);
    if (described.length === 1) {
      const sentence = described[0];
      const capitalized = sentence.charAt(0).toUpperCase() + sentence.slice(1);
      return capitalized.endsWith(".") ? capitalized : `${capitalized}.`;
    }
    return `Revisá los datos: ${described.join("; ")}.`;
  }

  if (NETWORK_PATTERN.test(text)) return NETWORK_MESSAGE;
  if (TECHNICAL_PATTERN.test(text) || text.startsWith("{") || text.startsWith("[")) {
    return fallback;
  }
  return text;
}
